import Joi from 'joi';
import { CHANGE_DATE_REQUEST_STATUS } from '../constants/ChangeDateRequestConstants';
import { DELIVERY_METHODS } from '../constants/DeliveryMethodsType';
import STATUS_TYPES from '../components/CalendarEvents/EditEventPage/SowStatus/constants/statusTypes';
import { PAGE_MODE } from '../components/shared/constants/pageModes';
import { EVENT_FLAGS } from '../constants/EventFlags';
import { EVENT_APPRAISER_ASSIGNMENT_ROLE } from '../constants/EventAppraiserAssignmentRole';
import ValidationHelperService from '../services/ValidationHelperService';

const EDIT_ERROR_MESSAGE = 'Please enter Required* fields.';

const SUBMIT_ERROR_MESSAGE = 'All fields must be entered.';
const SUBMIT_WHILE_HAVING_CHANGE_DATE_REQUEST_ERROR_MESSAGE =
  'Cannot submit event while change request is pending.';
const ASSIGNMENT_CHANGE_WITHIN_SIX_WEEKS_ERROR_MESSAGE =
  'Assignment changes cannot be made within 6 weeks of Appraisal date, contact MDDAP-support@ISACA.org for assistance.';

const READY_ERROR_MESSAGE_DC_ATL =
  'Candidate and/or Observer contracts need to be completed by ISACA/MDDAP.';
const READY_ERROR_MESSAGE_SOW_TABLE = 'Complete the SOW table before marking event as Ready.';

const ERROR_CODE_SOW_STATUSES_NOT_COMPLETED = 'SOW_STATUSES_NOT_COMPLETED';
const ERROR_CODE_SOW_STATUSES_CANNOT_BE_COMPLETED = 'SOW_STATUSES_CANNOT_BE_COMPLETED';
const ERROR_CODE_ASSIGNMENT_CHANGE_WITHIN_SIX_WEEKS = 'ASSIGNMENT_CHANGE_WITHIN_SIX_WEEKS';
const ERROR_CODE_EDIT_FIELDS_REQUIRED = 'EDIT_FIELDS_REQUIRED';

const SOW_STATUS_ERROR_MAP = {
  [ERROR_CODE_SOW_STATUSES_NOT_COMPLETED]: READY_ERROR_MESSAGE_SOW_TABLE,
  [ERROR_CODE_SOW_STATUSES_CANNOT_BE_COMPLETED]: READY_ERROR_MESSAGE_DC_ATL
};

const EDIT_ERROR_MAP = {
  [ERROR_CODE_EDIT_FIELDS_REQUIRED]: EDIT_ERROR_MESSAGE,
  [ERROR_CODE_ASSIGNMENT_CHANGE_WITHIN_SIX_WEEKS]: ASSIGNMENT_CHANGE_WITHIN_SIX_WEEKS_ERROR_MESSAGE
};

const CHANGE_DATE_REQUEST_SUBMITTED_STATUSES = [
  CHANGE_DATE_REQUEST_STATUS.PENDING_ADMIN_APPROVAL,
  CHANGE_DATE_REQUEST_STATUS.PENDING_FDA_APPROVAL
];

const EventInputValidator = {
  validateAddEventData: event => {
    return addEventSchema.validate(event, { abortEarly: true });
  },

  validateEditEventData: (event, existingEventData, options = {}) => {
    const errorStatus = {
      errors: null,
      messages: []
    };

    const { error } = editEventSchema.validate(event, {
      abortEarly: false,
      context: {
        isAtlPageMode: options.pageMode === PAGE_MODE.ISACA_APPRAISER_ATL,
        isWithinSixWeeks: existingEventData.EventFlags.includes(EVENT_FLAGS.UPCOMING_EVENT),
        originalAssignments: existingEventData.EventAssignments
      }
    });

    if (error) {
      errorStatus.errors = error;
      errorStatus.messages = ValidationHelperService.getValidationMessages(
        error.details,
        EDIT_ERROR_MAP,
        EDIT_ERROR_MESSAGE
      );
    }

    return errorStatus;
  },

  validateSubmitEventData: (event, existingEventData) => {
    const errorStatus = {
      errors: null,
      messages: []
    };

    const validator = submitEventSchema.validate(event, {
      abortEarly: false
    });

    const errors = validator.error;
    if (errors) {
      errorStatus.messages.push(SUBMIT_ERROR_MESSAGE);
      errorStatus.errors = errors;
    }

    if (validateIfChangeDateRequestStatusSubmitted(existingEventData)) {
      errorStatus.messages.push(SUBMIT_WHILE_HAVING_CHANGE_DATE_REQUEST_ERROR_MESSAGE);
    }

    return errorStatus;
  },

  validateReadyEventData: (event, editOnlyQualifiedAtms = false) => {
    const errorStatus = {
      errors: null,
      messages: []
    };

    const { error } = readyEventSchema.validate(event, {
      abortEarly: false,
      context: { editOnlyQualifiedAtms }
    });

    if (error) {
      errorStatus.errors = error;
      errorStatus.messages = ValidationHelperService.getValidationMessages(
        error.details,
        SOW_STATUS_ERROR_MAP
      );
    }

    return errorStatus;
  },

  validateReviewEventData: event => {
    const errorStatus = {
      errors: null,
      messages: []
    };

    const validator = baseEventSchemaWithAtl.validate(event, {
      abortEarly: false
    });

    const errors = validator.error;
    if (errors) {
      errorStatus.errors = errors;
    }

    return errorStatus;
  }
};

const baseEventSchema = Joi.object().keys({
  startDate: Joi.date().required(),
  endDate: Joi.date().required().min(Joi.ref('startDate')),
  orgUnitIds: Joi.array().items(Joi.number().min(0).required()).required(),
  domains: Joi.array().items(Joi.number())
});

const addEventSchema = baseEventSchema
  .keys({
    orgId: Joi.number().min(0).required(),
    atl: Joi.number().min(0).allow(null)
  })
  .unknown(true);

const baseSaveEventSchema = baseEventSchema.keys({
  eventId: Joi.number(),
  intendedStartDate: Joi.date().allow(null),
  intendedEndDate: Joi.date().allow(null).min(Joi.ref('intendedStartDate'))
});

const editEventSchema = baseSaveEventSchema
  .keys({
    atl: Joi.object().allow(null),
    eventTeams: Joi.array().items(
      Joi.object({
        role: Joi.number(),
        numberOfPeople: Joi.number().min(0).required(),
        notes: Joi.string().allow('', null)
      })
    ),
    logisticalNotes: Joi.string().allow('', null),
    deliveryMethod: Joi.number().allow(null),
    eventAssignments: Joi.array()
      .allow(null)
      .custom((value, helpers) => {
        const { isAtlPageMode, isWithinSixWeeks, originalAssignments } = helpers.prefs.context;

        if (
          isAtlPageMode &&
          isWithinSixWeeks &&
          hasAssignmentRoleChanges(value, originalAssignments)
        ) {
          return helpers.error('any.invalid');
        }

        return value;
      })
      .messages({
        'any.invalid': ERROR_CODE_ASSIGNMENT_CHANGE_WITHIN_SIX_WEEKS
      })
  })
  .unknown(true);

const submitEventSchema = baseSaveEventSchema
  .keys({
    atl: Joi.object().required(),
    eventTeams: Joi.array().items(
      Joi.object({
        role: Joi.number(),
        numberOfPeople: Joi.number().min(0).required(),
        notes: Joi.string().required()
      })
    ),
    logisticalNotes: Joi.string().required(),
    deliveryMethod: Joi.number().required(),
    eventAssignments: Joi.array().allow(null),
    regions: Joi.when('deliveryMethod', {
      is: DELIVERY_METHODS.VIRTUAL.value,
      then: Joi.any().allow(null),
      otherwise: Joi.object().when(
        Joi.object({
          eventTakesPlacesInAfrica: Joi.boolean().valid(false),
          eventTakesPlacesInAsia: Joi.boolean().valid(false),
          eventTakesPlacesInEurope: Joi.boolean().valid(false),
          eventTakesPlacesInNorthAmerica: Joi.boolean().valid(false),
          eventTakesPlacesInAustralia: Joi.boolean().valid(false),
          eventTakesPlacesInSouthAmerica: Joi.boolean().valid(false)
        }).unknown(true),
        {
          then: Joi.forbidden()
        }
      )
    })
  })
  .unknown(true);

const baseEventSchemaWithAtl = baseEventSchema
  .keys({
    atl: Joi.object().required()
  })
  .unknown(true);

const sowStatusItemSchema = Joi.object({
  id: Joi.number().required(),
  statusType: Joi.number().required().messages({
    '*': ERROR_CODE_SOW_STATUSES_NOT_COMPLETED
  }),
  contractTypes: Joi.array()
    .items()
    .min(1)
    .when('$editOnlyQualifiedAtms', {
      is: true,
      then: Joi.when('statusType', {
        is: Joi.number().valid(STATUS_TYPES.ATM),
        then: Joi.required().messages({
          '*': ERROR_CODE_SOW_STATUSES_NOT_COMPLETED
        }),
        otherwise: Joi.any().messages({
          '*': ERROR_CODE_SOW_STATUSES_CANNOT_BE_COMPLETED
        })
      }),
      otherwise: Joi.required().messages({
        '*': ERROR_CODE_SOW_STATUSES_NOT_COMPLETED
      })
    }),
  contractStatus: Joi.number().when('$editOnlyQualifiedAtms', {
    is: true,
    then: Joi.when('statusType', {
      is: Joi.number().valid(STATUS_TYPES.ATM),
      then: Joi.required().messages({
        '*': ERROR_CODE_SOW_STATUSES_NOT_COMPLETED
      }),
      otherwise: Joi.any().messages({
        '*': ERROR_CODE_SOW_STATUSES_CANNOT_BE_COMPLETED
      })
    }),
    otherwise: Joi.required().messages({
      '*': ERROR_CODE_SOW_STATUSES_NOT_COMPLETED
    })
  })
}).unknown(true);

const readyEventSchema = baseEventSchemaWithAtl
  .keys({
    sowStatuses: Joi.array().items(sowStatusItemSchema).required()
  })
  .unknown(true);

const validateIfChangeDateRequestStatusSubmitted = existingEventData =>
  CHANGE_DATE_REQUEST_SUBMITTED_STATUSES.includes(
    existingEventData.AppraiserEventChangeDateRequest?.Status
  );

const hasAssignmentRoleChanges = (currentAssignments, originalAssignments) => {
  if (!currentAssignments || !originalAssignments) {
    return false;
  }

  return currentAssignments.some(current => {
    const original = originalAssignments.find(
      o => o.Appraiser.ExternalUserId === current.externalUserId
    );

    const originalRole =
      original.EventAppraiserAssignment?.ActiveRole ?? EVENT_APPRAISER_ASSIGNMENT_ROLE.NA;

    return current.role !== originalRole;
  });
};

export default EventInputValidator;
